import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { WorkOrderCategory, WorkOrderListResponse } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';

const CATEGORIES: WorkOrderCategory[] = [
  'plumbing',
  'electrical',
  'hvac',
  'appliance',
  'structural',
  'pest',
  'landscaping',
  'safety',
  'general',
];

export function MaintenanceView({ data }: { data: WorkOrderListResponse }) {
  const queryClient = useQueryClient();
  const [category, setCategory] = useState<WorkOrderCategory>('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [permission, setPermission] = useState(false);
  const [availability, setAvailability] = useState('');
  const [ok, setOk] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      api.portal.workOrders.submit({
        category,
        title,
        description,
        priority: 'routine',
        permissionToEnter: permission,
        availability: availability || null,
      }),
    onSuccess: () => {
      setOk(true);
      setTitle('');
      setDescription('');
      setAvailability('');
      setPermission(false);
      void queryClient.invalidateQueries({ queryKey: ['portal', 'work-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'overview'] });
    },
  });

  return (
    <>
      <h1>Maintenance requests</h1>
      <p className="subtitle">Report a problem in your unit and track its progress.</p>

      <section aria-labelledby="new-heading">
        <h2 id="new-heading">Submit a new request</h2>
        <form
          className="card stack"
          onSubmit={(e) => {
            e.preventDefault();
            setOk(false);
            submit.mutate();
          }}
        >
          {ok && <StatusMessage tone="ok">Your request was submitted.</StatusMessage>}
          {submit.isError && (
            <StatusMessage tone="err">{errorMessage(submit.error)}</StatusMessage>
          )}

          <div>
            <label htmlFor="wo-category">Category</label>
            <select
              id="wo-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as WorkOrderCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="wo-title">Title</label>
            <input
              id="wo-title"
              value={title}
              required
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary (e.g. Leaking kitchen faucet)"
            />
          </div>

          <div>
            <label htmlFor="wo-desc">Description</label>
            <textarea
              id="wo-desc"
              value={description}
              required
              rows={4}
              maxLength={4000}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the problem in detail."
            />
          </div>

          <div>
            <label htmlFor="wo-avail">Availability (optional)</label>
            <input
              id="wo-avail"
              value={availability}
              maxLength={500}
              onChange={(e) => setAvailability(e.target.value)}
              placeholder="e.g. Weekday mornings before 11am"
            />
          </div>

          <div className="row">
            <input
              id="wo-permission"
              type="checkbox"
              style={{ width: 'auto' }}
              checked={permission}
              onChange={(e) => setPermission(e.target.checked)}
            />
            <label htmlFor="wo-permission" style={{ margin: 0 }}>
              I grant permission to enter to make this repair
            </label>
          </div>

          <div>
            <button type="submit" disabled={submit.isPending}>
              {submit.isPending ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      </section>

      <section aria-labelledby="list-heading">
        <h2 id="list-heading">Your requests</h2>
        {data.workOrders.length === 0 ? (
          <p className="muted">You have not submitted any maintenance requests.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Your maintenance requests and their status</caption>
              <thead>
                <tr>
                  <th scope="col">Request</th>
                  <th scope="col">Category</th>
                  <th scope="col">Status</th>
                  <th scope="col">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {data.workOrders.map((w) => (
                  <tr key={w.id}>
                    <td>{w.title}</td>
                    <td>{w.category}</td>
                    <td>
                      <span className="badge">{w.status}</span>
                    </td>
                    <td>{formatDate(w.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export function Maintenance() {
  const query = useQuery({
    queryKey: ['portal', 'work-orders'],
    queryFn: () => api.portal.workOrders.list(),
  });
  if (query.isLoading) return <Loading label="Loading your requests…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <MaintenanceView data={query.data} />;
}
